import { ETAPES, type Etape, type Foyer } from '@dinertournant/core';
import { membresPresents } from '@dinertournant/core';
import { IconePoignee } from './icones.js';
import { PilluleEtape } from './PastilleEtape.js';

interface Props {
  foyer: Foyer;
  /** Étape où ce foyer est placé (colonne), pour signaler un « hors choix ». */
  etapePlacee?: Etape | undefined;
  accordHorsDispo?: boolean | undefined;
  /** Toutes les étapes où la voiture reçoit (deux en cas d'exception forcée). */
  etapesRecues?: Etape[] | undefined;
  /** Alternative clavier au glisser-déposer : « Déplacer vers… ». */
  onDeplacer?: ((etape: Etape | null) => void) | undefined;
  /** Exception : la voiture reçoit aussi à cette étape (proposé tant qu'elle ne reçoit qu'à une). */
  onRecevoirAussi?: ((etape: Etape) => void) | undefined;
}

const VERS: Record<Etape, string> = { entree: "l'entrée", plat: 'le plat', dessert: 'le dessert' };
const AU: Record<Etape, string> = { entree: "à l'entrée", plat: 'au plat', dessert: 'au dessert' };

/** Carte d'hôte (écran 4) : nom, taille, étapes possibles ; signalée tant qu'un placement hors disponibilités n'a pas d'accord. */
export function CarteHote({ foyer, etapePlacee, accordHorsDispo = false, etapesRecues = [], onDeplacer, onRecevoirAussi }: Props) {
  const taille = membresPresents(foyer).length;
  const horsChoix = etapePlacee !== undefined && !foyer.disponibilites.includes(etapePlacee) && !accordHorsDispo;
  const deuxEtapes = etapesRecues.length >= 2;
  const autreReception = etapesRecues.find((e) => e !== etapePlacee);
  // Une voiture à deux étapes a deux cartes : l'identifiant du menu doit rester unique.
  const idMenu = `deplacer-${foyer.id}-${etapePlacee ?? 'reserve'}`;
  return (
    <article className={`carte-hote${horsChoix ? ' hors-choix' : ''}`} aria-label={`${foyer.nom}, ${taille} personne${taille > 1 ? 's' : ''}`}>
      <span className="poignee" aria-hidden="true">
        <IconePoignee />
      </span>
      <div className="contenu">
        <div className="entete">
          <span className="nom">{foyer.nom}</span>
          <span className="taille">
            {taille} pers.
            {foyer.capaciteMax ? ` · table de ${foyer.capaciteMax} max` : ''}
            {horsChoix ? <strong className="hors-choix-badge"> · hors choix</strong> : null}
          </span>
        </div>
        {deuxEtapes ? (
          <div>
            <span className="badge-deux-etapes" title={autreReception ? `Reçoit aussi ${AU[autreReception]}, à votre demande` : undefined}>
              2 étapes{autreReception ? ` · aussi ${AU[autreReception]}` : ''}
            </span>
          </div>
        ) : null}
        <div className="dispos">
          <span>Peut recevoir</span>
          {ETAPES.map((e) => {
            const possible = foyer.disponibilites.includes(e);
            return <PilluleEtape key={e} etape={e} etat={possible ? (e === etapePlacee ? 'pleine' : 'teinte') : 'vide'} />;
          })}
          {onDeplacer ? (
            <label className="sr-only" htmlFor={idMenu}>
              Déplacer {foyer.nom} vers
            </label>
          ) : null}
          {onDeplacer ? (
            <select
              id={idMenu}
              className="menu-deplacer"
              value={etapePlacee ?? ''}
              onChange={(ev) => {
                const v = ev.target.value;
                if (v.startsWith('aussi:')) onRecevoirAussi?.(v.slice(6) as Etape);
                else onDeplacer(v ? (v as Etape) : null);
              }}
            >
              <option value="">{deuxEtapes ? 'Ne plus recevoir ici' : 'Non placé'}</option>
              {ETAPES.filter((e) => e === etapePlacee || !etapesRecues.includes(e)).map((e) => (
                <option key={e} value={e}>
                  Déplacer vers {VERS[e]}
                </option>
              ))}
              {onRecevoirAussi && etapePlacee && !deuxEtapes
                ? ETAPES.filter((e) => e !== etapePlacee).map((e) => (
                    <option key={`aussi-${e}`} value={`aussi:${e}`}>
                      Recevoir aussi {AU[e]}…
                    </option>
                  ))
                : null}
            </select>
          ) : null}
        </div>
      </div>
    </article>
  );
}
